/**
 * SmokeTestPanel — One-click app health check.
 *
 * Iter 68 · Pedido del CEO tras el incidente de pérdida de datos: quiere
 * un botón que en 30s le diga "🟢 Todo OK / 🟡 Warnings / 🔴 Bugs" antes
 * de redesplegar o después de cambios. Consume `GET /api/admin/smoke-test`.
 *
 * Props:
 *   getAuthHeaders: fn · viene del contexto de auth
 */
import { useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Activity, Loader2, CheckCircle2, AlertCircle, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusIcon = ({ status }) => {
  if (status === 'ok') return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
  if (status === 'warn') return <AlertCircle className="w-4 h-4 text-amber-500" />;
  return <XCircle className="w-4 h-4 text-rose-600" />;
};

const OverallBadge = ({ overall, summary }) => {
  const bg = overall === 'ok' ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : overall === 'warn' ? 'bg-amber-50 text-amber-800 border-amber-200'
    : 'bg-rose-50 text-rose-800 border-rose-200';
  const label = overall === 'ok' ? '🟢 Todo OK'
    : overall === 'warn' ? '🟡 Con advertencias'
    : '🔴 Se detectaron problemas';
  return (
    <div className={`px-3 py-1.5 rounded-full border ${bg} text-xs font-bold`}>
      {label} · {summary?.ok || 0} ok · {summary?.warn || 0} warn · {summary?.fail || 0} fail
    </div>
  );
};

const SmokeTestPanel = ({ getAuthHeaders }) => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const run = async () => {
    setRunning(true); setErr(null);
    try {
      const r = await axios.get(`${API}/admin/smoke-test`, { headers: getAuthHeaders() });
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo ejecutar el smoke test');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="p-4 border-0 shadow-sm border-l-4 border-l-teal-500" data-testid="smoke-test-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2">
          <span className="p-1.5 rounded-lg bg-teal-100">
            <Activity className="w-4 h-4 text-teal-700" />
          </span>
          <div>
            <p className="text-sm font-bold text-slate-900">Smoke test rápido</p>
            <p className="text-xs text-slate-500">Verifica base de datos, motor financiero, wallets y guards de seguridad.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {result && <OverallBadge overall={result.overall} summary={result.summary} />}
          <Button
            onClick={run}
            disabled={running}
            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold"
            size="sm"
            data-testid="smoke-test-run-btn"
          >
            {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Activity className="w-3.5 h-3.5 mr-1.5" />}
            {running ? 'Ejecutando…' : 'Ejecutar test'}
          </Button>
        </div>
      </div>

      {err && (
        <p className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
          {err}
        </p>
      )}

      {result && (
        <div className="mt-3 space-y-1.5">
          {result.checks.map((c, i) => (
            <div
              key={`${c.name}-${i}`}
              className="flex items-start gap-2 text-xs p-2 rounded bg-slate-50 border border-slate-100"
              data-testid={`smoke-check-${i}`}
            >
              <StatusIcon status={c.status} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800">{c.name}</p>
                {c.detail && <p className="text-slate-500 truncate">{c.detail}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default SmokeTestPanel;
